import { create } from 'zustand';
import { db, auth } from '../config/firebase';
import { addDoc, collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { adminUpdateMandantRoot } from '../config/adminOverwriteGuard';
import useAppStore from './index';
import {
    buildMandantNr,
    extractCoreFromMandantNr,
    migrateMandantNrIfNeeded,
    normalizeVorabMandantNr,
    resolveCoachInitialLetter,
    tarifStatusFromTier,
} from '../utils/mandantNrFormat';
import { resolveTarifTier } from '../utils/clientPricing';
import { clearOwnedActiveSession, buildAdminLockPayload, type AdminLockHolder } from '../utils/sessionLock';
import {
    buildCoachAssignmentMailHtml,
    buildCoachAssignmentMailSubject,
    buildCoachAssignmentMailText,
} from '../utils/coachAssignmentMail';

/** Anzeigename des eingeloggten Team-Members für Wartungs-Lock. */
const resolveCurrentLockHolder = (): AdminLockHolder | null => {
    const user = auth.currentUser;
    if (!user?.uid) return null;
    const email = String(user.email || '').trim().toLowerCase();
    const { team = [], teamRegistry = [] } = useAppStore.getState();
    const pool = [...team, ...teamRegistry];
    const member =
        pool.find((m: any) => String(m?.id || '').trim() === user.uid) ||
        pool.find((m: any) => String(m?.email || '').trim().toLowerCase() === email) ||
        null;
    const name =
        String(member?.name || '').trim() ||
        String(user.displayName || '').trim() ||
        (email ? email.split('@')[0] : '') ||
        'PreisExpert-Team';
    return { uid: user.uid, name, email: email || undefined };
};
export interface PendingClient {
    id: string;
    firmenname?: string;
    vorname?: string;
    nachname?: string;
    email: string;
    betreuer?: string; 
    mandantNr?: string; 
    lizenzDetails?: { typ: string };
    isLegacy?: boolean;
    isArchived?: boolean;
    [key: string]: any;
}

export interface AdminTeamMember {
    id: string;
    name: string;
    email: string;
    role: string; 
    category: string;
    imageUrl?: string;
}

interface AdminState {
    pendingApprovals: PendingClient[]; 
    crmClients: PendingClient[];
    archivedClients: PendingClient[];
    isLoadingPending: boolean;
    error: string | null;
    adminTeam: AdminTeamMember[];
    impersonatedClientId: string | null; 
    impersonatedClientName: string | null;
    /**
     * true = startImpersonation lädt die Akte selbst.
     * App.tsx darf dann keinen zweiten initializeStore starten (sonst Race → FEHLER/Sync tot).
     */
    godModeInitOwnedByStarter: boolean;
    /** Session-Lock Blockade (fremde Session / entzogene sessionId) */
    isSessionBlocked: boolean;
    setSessionBlocked: (blocked: boolean) => void;
    fetchClients: () => Promise<void>;
    syncAdminTeam: () => void;
    updateTeamMemberRole: (memberId: string, newRole: string) => Promise<void>;
    assignCoachToClient: (clientId: string, coachId: string) => Promise<void>; 
    startImpersonation: (clientId: string, clientName: string) => Promise<void>; 
    stopImpersonation: () => Promise<void>; 
}

const mapStoreTeamToAdminTeam = (): AdminTeamMember[] => {
    const team = useAppStore.getState().team || [];
    return team.map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email || '',
        role: member.role || 'coach',
        category: member.category || 'coach',
        imageUrl: member.imageUrl,
    }));
};

const generateMandantNr = (allClients: PendingClient[], clientToMove: PendingClient, coachInitial: string) => {
    const settings = useAppStore.getState().settings;
    const targetYear = 2027;

    let maxNum = 0;
    allClients.forEach(c => {
        if (c.mandantNr && c.mandantNr.includes(`/${targetYear}`)) {
            const core = extractCoreFromMandantNr(c.mandantNr);
            const match = core.match(/^(\d+)\//);
            if (match && match[1]) {
                const num = parseInt(match[1], 10);
                if (!isNaN(num) && num > maxNum) maxNum = num;
            }
        }
    });

    const nextNum = (maxNum + 1).toString().padStart(2, '0');
    const tier = resolveTarifTier({
        settings,
        client: clientToMove,
        isSolo: clientToMove.isSolo === true,
        hasEmployeeEval: clientToMove.hasEmployeeEval === true,
        forceSoloTarif: !!clientToMove.forceSoloTarif,
        lizenzTyp: clientToMove.lizenzDetails?.typ,
    });

    return buildMandantNr({
        core: `${nextNum}/${targetYear}`,
        coachInitial,
        tarifStatus: tarifStatusFromTier(tier),
    });
};

const useAdminStore = create<AdminState>((set: any, get: any) => ({
    pendingApprovals: [],
    crmClients: [],
    archivedClients: [],
    isLoadingPending: false,
    error: null,
    
    // God-Mode überlebt Reload (Zustand stirbt bei Redirect — sessionStorage nicht).
    // Wird nur bei Logout / BEENDEN / Abbrechen gelöscht — nicht beim Team-Portal-Boot.
    impersonatedClientId: (() => {
        try { return sessionStorage.getItem('GOD_MODE_ID'); } catch { return null; }
    })(),
    impersonatedClientName: null,
    godModeInitOwnedByStarter: false,
    isSessionBlocked: false,
    setSessionBlocked: (blocked: boolean) => set({ isSessionBlocked: blocked }),
    
    adminTeam: [],

    syncAdminTeam: () => {
        set({ adminTeam: mapStoreTeamToAdminTeam() });
    },

    fetchClients: async () => {
        set({ isLoadingPending: true, error: null });
        try {
            const querySnapshot = await getDocs(collection(db, 'kalkulation_mandanten'));
            const allClients: PendingClient[] = [];
            
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const clientData = data.clientData || {};
                
                const cleanPart = (v: any) => {
                    const s = String(v ?? '').trim();
                    if (!s || s.toLowerCase() === 'unbekannt') return '';
                    return s;
                };
                const normalizeKey = (s: string) =>
                    s.toLowerCase().normalize('NFC').replace(/\s+/g, ' ').trim();
                const titleCaseIfScreaming = (s: string) => {
                    if (!s || s !== s.toUpperCase() || s.length < 2) return s;
                    return s
                        .split(/\s+/)
                        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
                        .join(' ');
                };
                const firmenname =
                    cleanPart(clientData.firmenname || clientData.salonname || clientData.unternehmen || data.firmenname) ||
                    'Kein Firmenname';
                const looksLikeSalonLabel = (vor: string) => {
                    const first = normalizeKey(vor).split(' ')[0] || '';
                    return ['friseur', 'friseurin', 'salon', 'haarstudio', 'hair', 'coiffeur', 'barber'].includes(first);
                };

                const contactVor = cleanPart(data.contact?.firstName || clientData.contact?.firstName);
                const contactNach = cleanPart(data.contact?.lastName || clientData.contact?.lastName);
                const unternehmer = cleanPart(clientData.unternehmername || data.unternehmername);
                const unterParts = unternehmer ? unternehmer.split(/\s+/).filter(Boolean) : [];
                const rawCombinedName = cleanPart(clientData.name || data.name);
                const nameParts = rawCombinedName ? rawCombinedName.split(/\s+/).filter(Boolean) : [];

                // Normale Personennamen zuerst (nicht Unternehmername – der ist oft GROSS geschrieben)
                let mappedVorname =
                    contactVor ||
                    cleanPart(data.vorname || clientData.vorname) ||
                    cleanPart(data.firstName || clientData.firstName) ||
                    (nameParts[0] || '') ||
                    '';
                let mappedNachname =
                    contactNach ||
                    cleanPart(data.nachname || clientData.nachname) ||
                    cleanPart(data.lastName || clientData.lastName) ||
                    (nameParts.slice(1).join(' ') || '') ||
                    cleanPart(clientData.displayName || data.displayName) ||
                    '';

                // Nur wenn leer: Unternehmername auffüllen
                if ((!mappedVorname || !mappedNachname) && unterParts.length >= 2) {
                    if (!mappedVorname) mappedVorname = titleCaseIfScreaming(unterParts[0] || '');
                    if (!mappedNachname) mappedNachname = titleCaseIfScreaming(unterParts.slice(1).join(' ') || '');
                }

                // Nur echte Salon-Labels (Friseur/Hair/…) umschreiben – Person===Salon ist OK (Solo)
                if (looksLikeSalonLabel(mappedVorname)) {
                    if (unterParts.length >= 2) {
                        mappedVorname = titleCaseIfScreaming(unterParts[0] || '');
                        mappedNachname = titleCaseIfScreaming(unterParts.slice(1).join(' ') || '');
                    } else {
                        const mailLocal = cleanPart(
                            clientData.email || clientData.mailadresse || data.email || data.mailadresse || ''
                        ).split('@')[0] || '';
                        const mailBits = mailLocal
                            .replace(/\d+/g, '')
                            .split(/[._-]+/)
                            .map((b) => b.trim())
                            .filter((b) => b.length > 1);
                        if (mailBits.length >= 2) {
                            const cap = (w: string) =>
                                w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
                            mappedVorname = cap(mailBits[0]);
                            mappedNachname = mailBits.slice(1).map(cap).join(' ');
                        }
                    }
                }

                const email = data.email || data.mailadresse || clientData.email || clientData.mailadresse || 'Keine E-Mail';
                
                const resolvedBetreuer = String(
                    data.betreuer ||
                    clientData.betreuer ||
                    data.coachId ||
                    clientData.coachId ||
                    data.coach ||
                    clientData.coach ||
                    ''
                ).trim();
                const mandantNr = data.mandantNr || clientData.mandantNr;

                allClients.push({ 
                    ...data,
                    id: docSnap.id, 
                    firmenname,
                    vorname: mappedVorname,
                    nachname: mappedNachname,
                    email,
                    betreuer: resolvedBetreuer,
                    coachId: data.coachId || clientData.coachId || resolvedBetreuer,
                    coach: data.coach || clientData.coach || resolvedBetreuer,
                    mandantNr,
                    clientData,
                    lizenzDetails: clientData.lizenzDetails,
                });
            });

            // Geister-Isolation: Deleted/Inaktiv komplett raus; Archived nicht in aktiven Listen
            const aliveClients = allClients.filter(
                (c) => c.isDeleted !== true && c.isActive !== false
            );
            const archived = aliveClients.filter((c) => c.isArchived === true);
            const activeClients = aliveClients.filter((c) => c.isArchived !== true);

            const crm = activeClients.filter(c => c.betreuer && c.betreuer.trim() !== '');
            const pending = activeClients.filter(c => !c.betreuer || c.betreuer.trim() === '');

            set({
                pendingApprovals: pending,
                crmClients: crm,
                archivedClients: archived,
                isLoadingPending: false,
                adminTeam: mapStoreTeamToAdminTeam(),
            });
        } catch (error: any) {
            console.error("❌ ERROR IN FETCHCLIENTS (FIRESTORE QUERY FAILED):", error, "Eingeloggte E-Mail:", auth?.currentUser?.email, "UID:", auth?.currentUser?.uid);
            set({ error: error.message, isLoadingPending: false });
        }
    },

    assignCoachToClient: async (clientId: string, coachId: string) => {
        const { pendingApprovals, crmClients } = get();
        const team = useAppStore.getState().team || [];
        const teamRegistry = useAppStore.getState().teamRegistry || [];
        const coachPool = [...team, ...teamRegistry].filter((member, index, arr) =>
            member?.id && arr.findIndex(m => m?.id === member.id) === index
        );
        const clientToMove = pendingApprovals.find((c: PendingClient) => c.id === clientId) || crmClients.find((c: PendingClient) => c.id === clientId);
        const selectedCoach = coachPool.find((member) => member.id === coachId);

        if (clientToMove && selectedCoach) {
            const initiale =
                resolveCoachInitialLetter(selectedCoach.name, coachPool) ||
                selectedCoach.name.charAt(0).toUpperCase();
            let newMandantNr = clientToMove.mandantNr;
            const allClients = [...pendingApprovals, ...crmClients];
            const isLegacyClient = !!(clientToMove.isLegacy || clientToMove.clientData?.isLegacy);
            const settings = useAppStore.getState().settings;

            if (isLegacyClient) {
                // Vorabkunden: auf `MA NN/YYYY-X` normalisieren (kein Solo/Team-Format)
                newMandantNr =
                    normalizeVorabMandantNr(clientToMove.mandantNr, initiale) ||
                    clientToMove.mandantNr;
            } else if (!newMandantNr || newMandantNr.trim() === '') {
                newMandantNr = generateMandantNr(allClients, clientToMove, initiale);
            } else {
                newMandantNr = migrateMandantNrIfNeeded({
                    existingNr: newMandantNr,
                    isLegacy: false,
                    core: extractCoreFromMandantNr(newMandantNr),
                    coachInitial: initiale,
                    client: clientToMove,
                    settings,
                    forceSoloTarif: !!clientToMove.forceSoloTarif,
                    isSolo: clientToMove.isSolo === true,
                    hasEmployeeEval: clientToMove.hasEmployeeEval === true,
                    lizenzTyp: clientToMove.lizenzDetails?.typ,
                });
            }

            const updatedClient = { ...clientToMove, betreuer: coachId, coachId, mandantNr: newMandantNr };

            set({
                pendingApprovals: pendingApprovals.filter((c: PendingClient) => c.id !== clientId),
                crmClients: [...crmClients.filter((c: PendingClient) => c.id !== clientId), updatedClient]
            });

            try {
                const payload: any = {
                    betreuer: coachId,
                    coachId,
                    'clientData.betreuer': coachId,
                    'clientData.coachId': coachId,
                };
                // Finalisierte Nummer mit Coach-Suffix immer persistieren
                if (newMandantNr) {
                    payload.mandantNr = newMandantNr;
                    payload['clientData.mandantNr'] = newMandantNr;
                    payload.customerNumber = newMandantNr;
                    payload['clientData.customerNumber'] = newMandantNr;
                }
                await adminUpdateMandantRoot(clientId, payload);

                // PEBase-Fusion: Betreuer-Mail an Mandanten (Worker: email + messageHtml)
                const clientEmail = String(
                    clientToMove.email ||
                        clientToMove.mailadresse ||
                        clientToMove.clientData?.email ||
                        clientToMove.clientData?.mailadresse ||
                        ''
                ).trim();
                const clientName = `${clientToMove.vorname || ''} ${clientToMove.nachname || ''}`.trim()
                    || String(clientToMove.firmenname || clientToMove.clientData?.salonname || '').trim()
                    || 'Mandant';
                const salonName = String(
                    clientToMove.firmenname ||
                        clientToMove.clientData?.salonname ||
                        clientToMove.clientData?.firmenname ||
                        clientName
                ).trim();
                const assignedCoachName = String(selectedCoach.name || '').trim() || 'dein Betreuer';
                const finalMandantennummer = String(newMandantNr || clientToMove.mandantNr || '').trim();

                if (clientEmail && finalMandantennummer) {
                    const mailHtml = buildCoachAssignmentMailHtml({
                        clientName,
                        salonName,
                        assignedCoachName,
                        finalMandantennummer,
                        pebaseLink: 'https://pebase.friseur-coaching.de/',
                        vimeoLink: 'https://vimeo.com/1229244105?share=copy&fl=sv&fe=ci',
                    });
                    const mailText = buildCoachAssignmentMailText({
                        clientName,
                        salonName,
                        assignedCoachName,
                        finalMandantennummer,
                        pebaseLink: 'https://pebase.friseur-coaching.de/',
                        vimeoLink: 'https://vimeo.com/1229244105?share=copy&fl=sv&fe=ci',
                    });
                    try {
                        await addDoc(collection(db, 'mail_queue'), {
                            type: 'coach_assigned_pebase',
                            email: clientEmail.toLowerCase(),
                            to: clientEmail.toLowerCase(),
                            subject: buildCoachAssignmentMailSubject(assignedCoachName),
                            html: mailHtml,
                            messageHtml: mailHtml,
                            text: mailText,
                            message: mailText,
                            mailTitle: 'Betreuer & PEBase',
                            participantName: clientName,
                            salonName,
                            appBaseUrl: typeof window !== 'undefined' ? window.location.origin : 'https://pe-one.friseur-coaching.de',
                            status: 'pending',
                            clientId,
                            finalMandantennummer,
                            createdAt: serverTimestamp(),
                        });
                    } catch (mailErr) {
                        console.error('coach_assigned_pebase mail_queue fehlgeschlagen:', mailErr);
                    }
                } else {
                    console.warn('coach_assigned_pebase übersprungen: E-Mail oder Mandant-Nummer fehlt', {
                        clientId,
                        hasEmail: !!clientEmail,
                        hasNr: !!finalMandantennummer,
                    });
                }
            } catch (error) {
                console.error("Fehler beim Speichern:", error);
            }
        }
    },

    updateTeamMemberRole: async (memberId: string, newRole: string) => {
        const { team: activeTeam, teamRegistry: registry } = useAppStore.getState();
        const team = activeTeam || [];
        const teamRegistry = registry || [];
        const member = team.find((entry) => entry.id === memberId)
            || teamRegistry.find((entry) => entry.id === memberId);
        if (member) {
            useAppStore.getState().updateTeamMember({ ...member, role: newRole, category: newRole });
        }
        set({ adminTeam: mapStoreTeamToAdminTeam() });
        try {
            const { team: activeTeam, teamRegistry: registry } = useAppStore.getState();
            await updateDoc(doc(db, 'system_data', 'global_settings'), {
                team: activeTeam,
                teamRegistry: registry || [],
            });
        } catch (error) {
            console.error('Fehler beim Speichern der Rolle in Firebase:', error);
            throw error;
        }
    },

    startImpersonation: async (clientId: string, clientName: string) => {
        const user = auth.currentUser;
        if (!user) {
            console.error('startImpersonation: kein auth.currentUser');
            return;
        }

        // Vektor 4: God-Mode Start — Timer killen, Epoch++, Store flashen, Gates sperren
        try {
            const { isolateSyncContext } = await import('../config/firebaseSync');
            isolateSyncContext();
        } catch (_) { /* ignore */ }

        // Session-Claim erst beim Akte-Öffnen (nicht schon auf Jahresübersicht) —
        // sonst fliegt der Mandant raus, obwohl Admin nur im Dashboard ist.
        // godModeInitOwnedByStarter: App.tsx-Effect darf KEINEN parallelen Init starten.
        set({
            impersonatedClientId: clientId,
            impersonatedClientName: clientName,
            isSessionBlocked: false,
            godModeInitOwnedByStarter: true,
        });
        sessionStorage.setItem('GOD_MODE_ID', clientId);
        // Freigabe-Schranke für diese Session umgehen
        try { sessionStorage.setItem('PE_ADMIN_UNLOCKED', '1'); } catch (_) { /* ignore */ }

        try {
            useAppStore.setState({ isSystemBooted: true });
        } catch (_) { /* ignore */ }

        // Akte SOFORT laden — nicht auf App-useEffect warten (der hat God-Mode mehrfach zerschossen)
        try {
            const init = useAppStore.getState().initializeStore;
            if (typeof init === 'function') {
                await init(clientId);
            }
        } catch (err) {
            console.error('God-Mode initializeStore fehlgeschlagen:', err);
            try {
                const { isolateSyncContext } = await import('../config/firebaseSync');
                isolateSyncContext();
            } catch (_) { /* ignore */ }
            try {
                useAppStore.setState({
                    isInitialized: true,
                    cloudWriteLock: true,
                    clientData: {
                        firmenname: clientName || 'God-Mode',
                    } as any,
                });
            } catch (_) { /* ignore */ }
        } finally {
            set({ godModeInitOwnedByStarter: false });
        }

        adminUpdateMandantRoot(clientId, buildAdminLockPayload(true, resolveCurrentLockHolder())).catch((err) => {
            console.error("Konnte Sperrriegel nicht setzen:", err);
        });
    },

    stopImpersonation: async () => {
        const { impersonatedClientId } = get();

        // Vor Kontext-Kill: offene Änderungen zwingend in die Cloud — sonst verwirft
        // isolateSyncContext den Debounce und die Akte ist nach BEENDEN wieder alt.
        if (impersonatedClientId) {
            try {
                const dirty =
                    !!(window as any).isSystemDirty || !!useAppStore.getState().isDirty;
                if (dirty) {
                    const { migrateLocalStateToCloud } = await import('../config/firebaseSync');
                    await migrateLocalStateToCloud();
                }
            } catch (flushErr) {
                console.error('God-Mode Flush vor BEENDEN fehlgeschlagen:', flushErr);
            }
        }
        
        // Vektor 4: God-Mode Stop — Timer killen, Epoch++, Store flashen (keine Restakte im RAM)
        try {
            const { isolateSyncContext } = await import('../config/firebaseSync');
            isolateSyncContext();
        } catch (_) { /* ignore */ }

        // Flag ZUERST löschen — verhindert Re-Entry nach F5/Login
        try { sessionStorage.removeItem('GOD_MODE_ID'); } catch (_) { /* ignore */ }

        // isolateSyncContext setzt isInitialized:false — Team-Portal muss sofort wieder
        // freigegeben werden, sonst nur leeres AutoUpdateModal (schwarzer Screen bis F5).
        useAppStore.setState({
            isInitialized: true,
            cloudWriteLock: false,
            isDirty: false,
            past: [],
            future: [],
        });
        set({
            impersonatedClientId: null,
            impersonatedClientName: null,
            godModeInitOwnedByStarter: false,
        });
        if (impersonatedClientId) {
            try {
                await clearOwnedActiveSession(impersonatedClientId, {
                    matchUserId: auth.currentUser?.uid || undefined,
                });
            } catch (error) {
                console.error("Session-Lock Freigabe fehlgeschlagen:", error);
            }
            try {
                await adminUpdateMandantRoot(impersonatedClientId, buildAdminLockPayload(false));
            } catch (error) {
                console.error("FATALER FEHLER BEIM ENTSPERREN IN FIREBASE:", error);
            }
        }
    }
}));

export default useAdminStore;
